import { catchError, EMPTY, filter, map, throwError } from 'rxjs';
import scopeLogger from '../../lib/scope-logger';
import { Kibana } from '#/utils/trackKibana.js';
import { createInteractiveEvent } from './create-interactive-event.js';
import { EventsList } from './events-list.js';

export default EventsProvider;
/**
 * Keep events
 * @type {EventsList<string, InteractiveEvent>}
 */
const eventsList = new EventsList();

const console = scopeLogger('RMI: Events manager');
const kibana = new Kibana('events manager');

const selectHostMessages = filter((event) => event.topic === '/host-action');

const selectEdgeMessages = filter(
  (message) => message.payload.type === 'promo',
);

/**
 * @param events$
 * @param creativeProvider
 * @param {function} makeContentRequest
 * @constructor
 */
function EventsProvider({ mns, events$, creativeProvider, store, renderersRegistry }) {
  let currentObjectIdentifier = null;

  events$.pipe(selectHostMessages).subscribe(handleHostMessage);
  events$.pipe(selectEdgeMessages).subscribe(handleEdgeMessage);

  window.addEventListener('beforeunload', () => {
    eventsList.forEach((event) => {
      if (['pending', 'prepare', 'await'].includes(event.state)) {
        kibana.warn('page closed before event shown', {
          eventState: event.state,
          interactive_id: event.interactive_id,
          binding: event.binding,
          video_id: event.videoId,
        });
      }
    });
  });

  let stabilityLevel = 0;
  const stabilityThreshold = 5;

  function handleHostMessage(event) {
    const { cmd, type, data } = event.payload;
    switch (true) {
      case cmd === 'start': {
        if (currentObjectIdentifier === data.video_id) {
          return;
        }
        Kibana.setContext('currentObjectIdentifier', data.video_id);
        kibana.info('current object changed, clear binding list', {
          oldObjectId: currentObjectIdentifier,
          bindings: Array.from(eventsList.keys()),
        });
        stabilityLevel = 0;
        eventsList.clear();
        currentObjectIdentifier = data.video_id;
        break;
      }

      case cmd === 'update' && type === 'video-position': {
        Kibana.setContext('currentObjectPosition', data.position);
        stabilityLevel = data.position_delta === 1 ?
          Math.min(stabilityLevel + 1, stabilityThreshold)
          : 0;

        if (stabilityLevel === stabilityThreshold) {
          onUpdatePosition(data.position, events$.next.bind(events$));
        } else {
          console.info('Stability threshold not reached, skip update position');
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * @typedef {Object} EdgeMessage
   * @property {string} topic
   * @property {Object} payload
   * @property {'create'|'delete'|'targeted'} payload.cmd
   * @property {Object} payload.data
   * @property {Object} payload.data.params
   * @property {Object} payload.data.params.binding
   * @property {string} payload.data.params.binding.id
   * @property {string} payload.data.params.video_id
   * @property {string} payload.data.interactive_id
   */

  /**
   * @param {EdgeMessage} message
   */
  function handleEdgeMessage(message) {
    const { cmd, data } = message.payload;

    if (cmd === 'create') {
      const {
        params: { binding = {}, video_id },
        interactive_id,
      } = data;

      try {
        const interactiveEvent = createInteractiveEvent(
          message.topic,
          binding,
          interactive_id,
          video_id,
        );
        if (eventsList.set(binding.id, interactiveEvent)){
          kibana.info('create event', interactiveEvent);
        } else {
          console.warn('Event already exists, skip', interactiveEvent);
        }
      } catch (error) {
        console.error(error);
        kibana.error(error, { binding, interactive_id, video_id });
      }
    }

    if (cmd === 'delete') {
      eventsList.delete(data.params.binding.id);
      kibana.debug('delete event', {binding: data.params.binding});
    }

    if (cmd === 'targeted') {
      const event = eventsList.get(data.params.binding.id);
      if (!event) {
        kibana.error('targeted event not found', {
          interactive_id: data.interactive_id,
          binding: data.params?.binding,
          video_id: data.params?.video_id,
        });
        return;
      }

      event.target = data.creative;
      event.dispatch.hit();

      creativeProvider
        .getByUrl(data.creative.url)
        .pipe(
          catchError(onFetchFailure),
          map(toEvent),
        )
        .subscribe({
          next(event) {
            event.state = 'await';
          },
          error(error) {
            // the only one "expected" error is "no event",
            // but who knows what could happen
            kibana.error(error, {
              binding: data.params.binding,
              creative: data.creative,
            });
            console.error(error);
          },
          complete() {
            console.info('Fetching creative finished');
          },
        });

      function toEvent(creative) {
        const event = eventsList.get(data.params.binding.id);
        const renderer = renderersRegistry.getRenderer(creative);

          //I think not found and not allowed its different errors
        if (!renderer) {
          event.dispatch.error('no renderer found');
          return EMPTY;
        }

        event.creative = creative;
        event.renderer = renderer;

        if(true !== renderer.allowed) {
          event.dispatch.error('render not allowed');
        }

        return event;
      }

      function onFetchFailure(error) {
        const event = eventsList.get(data.params.binding.id);
        if (!event) {
          return throwError(() => {
            return new Error('no event to attach creative');
          });
        } else {
          const { status, message } = error;
          const newError = new Error(message || 'Creative fetch failure');
          newError.url = data.creative.url;
          newError.status = status;
          event.dispatch.error(newError);
          return EMPTY;
        }
      }
    }
  }

  function onUpdatePosition(position, cb) {
    const now = Date.now();

    eventsList.forEach((event, _id) => {
      if (event.state === 'deleted') {
        // TODO: ensure it is safe, then real remove from list
        //  - unsafe case: event deleted, but still in use or have some pending process

        /* for the moment just do nothing with deleted events */
        return;
      }
      if (now >= event.expiresAt) {
        event.state = 'deleted';
        return;
      }

      const [start, end] = event.interval;
      let diff = Math.min(0, position - start);
      diff ||= Math.max(0, position - end);

      const hasPositionInsideInterval = diff === 0;
      if (hasPositionInsideInterval) {
        switch (event.state) {
          case 'idle':
            try {
              // TODO: Does it make sense to set timeout
              //  to report about "failed request",
              //  e.g. no targeting message in X seconds
              mns.publish(store.getState().app.contentRequestTopic, {
                promo_id: event.interactive_id,
                binding: event.binding,
              });
              event.state = 'pending';
              kibana.debug('request targeting', {
                interactive_id: event.interactive_id,
                binding: event.binding,
              });
            } catch (err) {
              console.info(`request targeting failure due to ${err.message}`);
            }
            break;
          case 'await':
            if (event.renderer.locked || !event.renderer.allowed) {
              return;
            }
            cb({
              topic: event.origin,
              payload: {
                cmd: 'display',
                type: event.renderer.name,
                data: {
                  creative: event.creative,
                  binding: event.binding,
                },
              },
            });
            break;
          default:
        }
        return;
      }

      switch (event.state) {
        case 'display':
          cb({
            topic: event.origin,
            payload: {
              cmd: 'close',
              type: event.renderer.name,
              data: {
                reason: 'out of bounds',
                diff,
              },
            },
          });
          break;
        case 'pending':
        case 'await':
        case 'prepare':
          event.dispatch.leaveSlot(diff);
          break;
        case 'shown':
        case 'failed':
          event.dispatch.leaveSlot();
          break;
        default:
      }
    });
  }
}
